import React, { useState, useEffect } from 'react';
import {
  DashboardOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { vpnAPI } from '@/api/endpoints/vpn.js';
import styles from '@/styles/pages/vpn/DashboardPage.module.css';

const DashboardPage = () => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Загрузка метрик
  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const response = await vpnAPI.metrics_get(false);
      if (response.success && response.data) {
        setMetrics(response.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки метрик:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  // Загрузка истории для выбранного ключа
  const fetchHistory = async (outlineKeyId) => {
    setLoadingHistory(true);
    try {
      const response = await vpnAPI.metrics_history_get(outlineKeyId, false);
      if (response.success && response.data) {
        setHistory(response.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleKeySelect = (key) => {
    setSelectedKey(key);
    fetchHistory(key.outline_key_id);
  };

  // Форматирование байт в человекочитаемый вид
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Б';
    const k = 1024;
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Получение иконки статуса (по трафику)
  const getStatusIcon = (totalBytes) => {
    if (totalBytes === 0) return <WarningOutlined className={styles.statusInactive} />;
    if (totalBytes < 1024 * 1024 * 10) return <SwapOutlined className={styles.statusLow} />;
    if (totalBytes < 1024 * 1024 * 100) return <SwapOutlined className={styles.statusMedium} />;
    return <CheckCircleOutlined className={styles.statusHigh} />;
  };

  // Подсчет общей статистики
  const getTotalStats = () => {
    const totalBytes = metrics.reduce((sum, key) => sum + (key.total_bytes || 0), 0);
    const activeKeys = metrics.filter(key => (key.total_bytes || 0) > 0).length;
    const avgBytes = metrics.length > 0 ? totalBytes / metrics.length : 0;
    return { totalBytes, activeKeys, avgBytes };
  };

  const stats = getTotalStats();

  // Группировка истории по датам
  const groupHistoryByDate = () => {
    const grouped = {};
    history.forEach(log => {
      const date = new Date(log.recorded_at).toLocaleDateString('ru-RU');
      if (!grouped[date]) {
        grouped[date] = 0;
      }
      grouped[date] += log.bytes_transferred;
    });
    return grouped;
  };

  const historyGrouped = groupHistoryByDate();
  const maxTraffic = Math.max(...Object.values(historyGrouped), 1);

  return (
    <div className={styles.container}>
      <div className={styles.bgGradient}></div>
      <div className={styles.bgGrid}></div>

      <div className={styles.content}>
        {/* Шапка */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <DashboardOutlined className={styles.headerIcon} />
            <h1 className={styles.title}>Дашборд трафика</h1>
          </div>
          <div className={styles.headerStats}>
            <div className={styles.headerStat}>
              <span className={styles.headerStatValue}>{metrics.length}</span>
              <span className={styles.headerStatLabel}>Ключей</span>
            </div>
            <div className={styles.headerStatDivider}></div>
            <div className={styles.headerStat}>
              <span className={styles.headerStatValue}>{stats.activeKeys}</span>
              <span className={styles.headerStatLabel}>Активных</span>
            </div>
          </div>
        </div>

        {/* Карточки статистики */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <SwapOutlined />
            </div>
            <div className={styles.statContent}>
              <span className={styles.statLabel}>Общий трафик</span>
              <span className={styles.statValue}>{formatBytes(stats.totalBytes)}</span>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <KeyOutlined />
            </div>
            <div className={styles.statContent}>
              <span className={styles.statLabel}>Средний трафик на ключ</span>
              <span className={styles.statValue}>{formatBytes(stats.avgBytes)}</span>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <CheckCircleOutlined />
            </div>
            <div className={styles.statContent}>
              <span className={styles.statLabel}>Активность</span>
              <span className={styles.statValue}>
                {Math.round((stats.activeKeys / (metrics.length || 1)) * 100)}%
              </span>
            </div>
          </div>
        </div>

        {/* Основная сетка: список ключей + график */}
        <div className={styles.mainGrid}>
          {/* Левая колонка - список ключей */}
          <div className={styles.keysPanel}>
            <div className={styles.panelHeader}>
              <h2>Ключи доступа</h2>
              <span className={styles.panelCount}>{metrics.length} всего</span>
            </div>

            {loading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <p>Загрузка ключей...</p>
              </div>
            ) : metrics.length === 0 ? (
              <div className={styles.emptyState}>
                <KeyOutlined className={styles.emptyIcon} />
                <p>Нет данных о ключах</p>
              </div>
            ) : (
              <div className={styles.keysList}>
                {metrics.map((key) => (
                  <div
                    key={key.outline_key_id}
                    className={`${styles.keyCard} ${selectedKey?.outline_key_id === key.outline_key_id ? styles.keyCardActive : ''}`}
                    onClick={() => handleKeySelect(key)}
                  >
                    <div className={styles.keyCardHeader}>
                      <div className={styles.keyCardInfo}>
                        <span className={styles.keyCardName}>{key.name}</span>
                        <span className={styles.keyCardId}>{key.outline_key_id.slice(0, 8)}...</span>
                      </div>
                      {getStatusIcon(key.total_bytes)}
                    </div>
                    <div className={styles.keyCardStats}>
                      <div className={styles.keyCardStat}>
                        <span className={styles.keyCardStatLabel}>Трафик</span>
                        <span className={styles.keyCardStatValue}>{formatBytes(key.total_bytes || 0)}</span>
                      </div>
                    </div>
                    <div className={styles.keyCardProgress}>
                      <div
                        className={styles.keyCardProgressBar}
                        style={{ width: `${Math.min((key.total_bytes / stats.totalBytes) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Правая колонка - график истории */}
          <div className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <h2>История трафика</h2>
              {selectedKey && (
                <span className={styles.selectedKeyName}>{selectedKey.name}</span>
              )}
            </div>

            {!selectedKey ? (
              <div className={styles.selectPrompt}>
                <DashboardOutlined className={styles.selectIcon} />
                <p>Выберите ключ из списка слева</p>
                <span>чтобы увидеть историю трафика</span>
              </div>
            ) : loadingHistory ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <p>Загрузка истории...</p>
              </div>
            ) : history.length === 0 ? (
              <div className={styles.emptyState}>
                <SwapOutlined className={styles.emptyIcon} />
                <p>Нет данных об истории трафика</p>
                <span>для ключа {selectedKey.name}</span>
              </div>
            ) : (
              <div className={styles.chartContainer}>
                {/* Барчарт (простой CSS график) */}
                <div className={styles.barChart}>
                  {Object.entries(historyGrouped).map(([date, bytes]) => (
                    <div key={date} className={styles.barWrapper}>
                      <div className={styles.barLabel}>{date}</div>
                      <div className={styles.barTrack}>
                        <div
                          className={styles.barFill}
                          style={{ width: `${(bytes / maxTraffic) * 100}%` }}
                        >
                          <span className={styles.barValue}>{formatBytes(bytes)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Таблица истории */}
                <div className={styles.historyTable}>
                  <div className={styles.historyHeader}>
                    <span>Дата и время</span>
                    <span>Трафик</span>
                  </div>
                  <div className={styles.historyList}>
                    {history.slice().reverse().map((log, idx) => (
                      <div key={idx} className={styles.historyRow}>
                        <span className={styles.historyDate}>
                          {new Date(log.recorded_at).toLocaleString('ru-RU')}
                        </span>
                        <span className={styles.historyBytes}>
                          {formatBytes(log.bytes_transferred)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;